import { useState } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import useGetAlertsAggregation from '../../API/hooks/alerts/useGetAlertsAggregation';
import useGetDriverById from '../../API/hooks/drivers/useGetDriverById';
import usePostLearnDriverFace from '../../API/hooks/drivers/usePostLearnDriverFace';
import useDeleteDriver from '../../API/hooks/drivers/useDeleteDriver';
import useGetDriverScoreDay from '../../API/hooks/drivers/useGetDriverScoreDay';
import useGetDriverScoreYears from '../../API/hooks/drivers/useGetDriverScoreYears';
// import useGetDeviceLocations from '../../API/hooks/maps/useGetDeviceLocations';
import useGetDriverLocations from '../../API/hooks/maps/useGetDriverLocations';
import useGetTrips from '../../API/hooks/trips/useGetTrips';
import useDriverThumbnail from '../../theme/useDriverThumbnail';
import useVehicleThumbnail from '../../theme/useVehicleThumbnail';
import Icon from '../../components/common/Icon/Icon';
import ImagePreview from '../../components/common/ImagePreview/ImagePreview';
import Modal from '../../components/common/Modal/Modal';
import { IconKeys } from '../../components/common/Icon/IconKeys';
import KnobChartWidget from '../../components/common/KnobChartWidget/KnobChartWidget';
import DetailsPageSkeleton from '../../components/common/Skeleton/DetailsPageSkeleton';
import Tags from '../../components/common/Tags';
import HiddenDetails, { HiddenDetailTagItem } from '../../components/common/Tags/HiddenDetails/HiddenDetails';
import ScoreTrendWidget from '../../components/common/ScoreTrend/ScoreTrend';
import MapWidget from '../../components/common/Map/Map';
import TimeSelection from '../../components/common/Toolbar/TimeSelection';
import { Button } from '../../components/ui/Button/Button';
import HeaderWrapper from '../../components/ui/DetailsPageHeaderWrapper/HeaderWrapper';
import {
  getEndOfTheDay,
  getFormattedDate, getStartOfMonth, getTimeRangeText,
  getPeriodDisplayLabel, isSingleDayPeriod,
} from '../../utils/dateUtils';
import { DriverScoreYears } from '../../API/types/Drivers';
import S from './DriverDetailsStyles';
import { ThemeType } from '../../theme/themeType';
import { convertMsToKm, convertMsecToHs, generateDistance } from '../../utils/unitsUtils';
import { getFilterFields, getSimpleEqQuery, queryFilters } from '../../utils/queryFilters';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import useAssignVehicleToDriver from '../../API/hooks/vehicles/useAssignVehicleToDriver';
import LearnDriverMessage from './Modals/LearnDriverMessage/LearnDriverMessage';
import DeleteModal from '../../components/common/DeleteModal/DeleteModal';
import DetailsWidget from '../../components/common/DetailsWidget/DetailsWidget';
import EditDriver from './Modals/EditDriver/EditDriver';
import CreateNote from '../../components/Notes/CreateNote/CreateNote';
import useGetRole from '../../API/hooks/useGetRole';
import { logger } from '../../API/services/SharedService';
import {
  changeObjectKeysWithPrefix, createCSV, downloadFile, generateEventsLabel, generateScoreColor, getScoreLabel,
} from '../../utils/commonUtils';
import useDeleteLearnDriverFace from '../../API/hooks/drivers/useDeleteLearnDriverFace';
import {
  AlertRisksDognutConfig, AlertRisksKeys, AlertTypesDognutConfig, AlertTypesKeys,
} from '../../API/types/Alerts';
import useGetAlertRisks from '../../API/hooks/alerts/useGetAlertsRisks';
import DoghnutChartWidget from '../../components/common/DoghnutChartWidget/DoghnutChartWidget';
import useGetAlerts from '../../API/hooks/alerts/useGetAlerts';
import useGetTripsAverageSpeedByDriverId from '../../API/hooks/trips/useGetTripsAverageSpeedByDriverId';
import useGetTripsNumberByDriverId from '../../API/hooks/trips/useGetTripsNumberByDriverId';
import useGetTripsDurationByDriverId from '../../API/hooks/trips/useGetTripsDurationByDriverId';
import useGetAlertTypes from '../../API/hooks/alerts/useGetAlertsTypes';
import DrivingHoursTracker from '../../components/DHT/DrivingHoursTracker';
import { FeaturesManagement } from '../../featuresControl';
import { getLocalStorageKey } from '../../utils/storageUtils';
import usePostDriverAvatarRequest from '../../API/hooks/drivers/usePostDriverAvatarRequest';
import useGetFleet from '../../API/hooks/useGetFleetDetails';
import InfoModal from '../../components/common/InfoModal/InfoModal';
import { driverDetailsPageFilters } from '../../constants/FilterDefault';
import { ENTITY_DETAILS_MAP_ALERTS_LIMIT } from '../../constants/EntityDetailsAlerts';
import useLocalStorage from '../../API/hooks/useLocalStorage';
import useGetActiveTripById from '../../API/hooks/trips/useGetActiveTripById';
import ActiveTripTags from '../../components/common/ActiveTripTags/ActiveTripTags';
import useDetailsListNavigation from '../../components/common/DetailsListNavigation/DetailsListNavigation';

function DriverDetailsPage(): JSX.Element {
  const theme = useTheme() as ThemeType;
  const vehicleThumbnail = useVehicleThumbnail();
  const driverThumbnail = useDriverThumbnail();

  const alertRisksChartConfig: AlertRisksDognutConfig = {
    cards: {
      urgent: {
        color: theme.charts.alertRiskUrgent,
        label: 'Urgent',
      },
      medium: {
        color: theme.charts.alertRiskMedium,
        label: 'Medium',
      },
      low: {
        color: theme.charts.alertRiskLow,
        label: 'Low',
      },
    },
    title: 'Alert risks',
  };

  const alertTypesChartConfig: AlertTypesDognutConfig = {
    cards: {
      IMU: {
        color: theme.charts.alertImu,
        label: 'IMU',
      },
      frontCamera: {
        color: theme.charts.alertFrontCamera,
        label: 'Front cam',
      },
      inwardCamera: {
        color: theme.charts.alertInwardCamera,
        label: 'Inw cam',
      },
      cloud: {
        color: theme.charts.alertCloud,
        label: 'Cloud',
      },
    },
    title: 'Alert types',
  };

  const fleetId = useGetFleetId();
  const { isFleetViewer } = useGetRole();
  const { user: { username } } = useAuthenticator();
  const navigate = useNavigate();
  const { id: driverId } = useParams();
  const listNavigation = useDetailsListNavigation({
    entity: 'drivers',
    currentId: driverId,
  });
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab');
  const isWideMapTab = !activeTab || activeTab === 'calendar' || activeTab === 'vehicle' || activeTab === 'alert';
  const [filters, setFilters] = useLocalStorage(getLocalStorageKey('drivers-page-details'), driverDetailsPageFilters);
  const [postLearnDriverOpen, togglePostLearnDriverOpen] = useState(false);
  const [editModalOpen, toggleEditModalOpen] = useState(false);
  const [deleteModalOpen, toggleDeleteModalOpen] = useState(false);
  const [confirmLearnFaceModalOpen, toggleConfirmLearnFaceOpen] = useState(false);
  const [learnFaceInProgress, setLearnFaceInProgress] = useState(false);
  const [confirmDeleteFaceModalOpen, toggleConfirmDeleteFaceModalOpen] = useState(false);
  const [addNoteModalOpen, toggleAddNoteModalOpen] = useState(false);
  const [dhtModalOpen, toggleDHTModalOpen] = useState(false);
  const [requestAvatarModalOpen, toggleRequestAvatarModalOpen] = useState(false);
  const [requestAvatarInProgress, setRequestAvatarInProgress] = useState(false);
  const [deviceOfflineModalOpen, toggleDeviceOfflineSaveModal] = useState(false);
  const [confirmSubmitModalOpen, toggleConfirmSubmitModal] = useState(false);
  const [photoPreviewOpen, togglePhotoPreviewOpen] = useState(false);
  const { data: fleetData } = useGetFleet(fleetId);
  const { data: averageSpeed } = useGetTripsAverageSpeedByDriverId(fleetId, driverId as string);
  const { data: tripsDuration } = useGetTripsDurationByDriverId(fleetId, driverId as string);
  const { data: tripsNumber } = useGetTripsNumberByDriverId(fleetId, driverId as string);
  const { data: driver, isLoading, error } = useGetDriverById(driverId as string, fleetId);
  const { data: driverEvents } = useGetAlertsAggregation(fleetId, `?limit=1000&skip=0${getSimpleEqQuery('driverId', driverId, 3)}`);
  const { data: risksChartData } = useGetAlertRisks(fleetId, `?limit=1000&skip=0${getSimpleEqQuery('driverId', driverId, 3)}${getFilterFields(filters, ['period'], 'eventDate')}`);
  const { data: typesChartData } = useGetAlertTypes(fleetId, `?limit=1000&skip=0${getSimpleEqQuery('driverId', driverId, 3)}${getFilterFields(filters, ['period'], 'eventDate')}`);
  const activeFilters = filters || driverDetailsPageFilters;
  const filterPeriod = activeFilters.period?.[0];
  const filterStartDate = activeFilters.startDate?.[0];
  const filterEndDate = activeFilters.endDate?.[0];

  const isDayMode = filterPeriod === 'daily';
  const isCustomSingleDay = filterPeriod === 'custom' && filterStartDate && filterEndDate
    && new Date(filterStartDate).toDateString() === new Date(filterEndDate).toDateString();
  const useHourlyMode = isDayMode || !!isCustomSingleDay;

  const toLocalDateStr = (iso: string): string => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const scoreDayDateResolved = filterStartDate ? toLocalDateStr(filterStartDate) : toLocalDateStr(new Date().toISOString());
  const scoreDayDate = useHourlyMode ? scoreDayDateResolved : '';

  const scoreDayDatePrev = useHourlyMode && scoreDayDate
    ? (() => { const d = new Date(`${scoreDayDate}T00:00:00Z`); d.setUTCDate(d.getUTCDate() - 1); return d.toISOString().slice(0, 10); })()
    : '';

  const scoreDayDateNext = useHourlyMode && scoreDayDate
    ? (() => { const d = new Date(`${scoreDayDate}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 1); return d.toISOString().slice(0, 10); })()
    : '';

  const currentYear = new Date().getFullYear();
  const { data: driverScoreDay } = useGetDriverScoreDay(driverId as string, fleetId, scoreDayDate);
  const { data: driverScoreDayPrev } = useGetDriverScoreDay(driverId as string, fleetId, scoreDayDatePrev);
  const { data: driverScoreDayNext } = useGetDriverScoreDay(driverId as string, fleetId, scoreDayDateNext);
  const { data: driverScoreYears } = useGetDriverScoreYears(driverId as string, fleetId, 2020, currentYear);

  type DaySnap = { date: string; score: number; drivingTimeMs: number };
  const allDays: DaySnap[] = (driverScoreYears as DriverScoreYears | undefined)?.years?.flatMap((y) => y.days as DaySnap[]) ?? [];

  const getFilteredDays = (): DaySnap[] => {
    if (useHourlyMode) return [];
    if (filterPeriod === 'custom' && filterStartDate && filterEndDate) {
      const start = new Date(filterStartDate).getTime();
      const end = new Date(filterEndDate).getTime();
      return allDays.filter((d) => { const t = new Date(d.date).getTime(); return t >= start && t <= end; });
    }
    if (filterPeriod === 'weekly') {
      const weekStart = new Date(filterStartDate || '').getTime();
      return allDays.filter((d) => new Date(d.date).getTime() >= weekStart && new Date(d.date).getTime() <= Date.now());
    }
    if (filterPeriod === 'monthly') {
      const monthStart = new Date(filterStartDate || getStartOfMonth().toISOString()).getTime();
      return allDays.filter((d) => new Date(d.date).getTime() >= monthStart && new Date(d.date).getTime() <= Date.now());
    }
    return allDays;
  };

  const filteredDays = getFilteredDays();

  const buildHourlyTrendData = (): { scoreDate: string; score: number }[] => {
    if (!useHourlyMode || !scoreDayDate) return [];

    type Snap = { hour: number; minute?: number; score: number; drivingTimeMs: number; weightSum: number };
    const snapshotsToPoints = (dayDate: string, snapshots: Snap[]): { scoreDate: string; score: number; ts: number }[] => snapshots.map((s) => {
      const base = new Date(`${dayDate}T00:00:00Z`);
      base.setUTCHours(s.hour, s.minute ?? 0, 0, 0);
      return { scoreDate: base.toISOString(), score: s.score, ts: base.getTime() };
    });

    const allPoints = [
      ...(driverScoreDay?.hourlySnapshots?.length ? snapshotsToPoints(scoreDayDate, driverScoreDay.hourlySnapshots) : []),
    ]
      .sort((a, b) => a.ts - b.ts)
      .map(({ scoreDate, score }) => ({ scoreDate, score }));

    if (allPoints.length) return allPoints;

    if (driverScoreDay?.score != null) {
      return [{ scoreDate: new Date(scoreDayDate).toISOString(), score: driverScoreDay.score }];
    }
    return [];
  };

  const driverTrendData: { scoreDate: string; score: number }[] = useHourlyMode
    ? buildHourlyTrendData()
    : filteredDays.map((d) => ({ scoreDate: d.date, score: d.score }));

  const weightedAvgScore = (days: DaySnap[]): number | null => {
    const totalDrivingTime = days.reduce((sum, d) => sum + (d.drivingTimeMs ?? 0), 0);
    if (totalDrivingTime <= 0) return null;
    const weightedSum = days.reduce((sum, d) => sum + d.score * (d.drivingTimeMs ?? 0), 0);
    return weightedSum / totalDrivingTime;
  };

  const averageScoreFromDays = weightedAvgScore(filteredDays);
  const driverAverageScore = useHourlyMode ? (driverScoreDay?.score ?? null) : averageScoreFromDays;

  const driverAllTimeAverage = weightedAvgScore(allDays);
  const { data: activeTrips } = useGetTrips(fleetId, `?limit=10&skip=0&sortField=createdAt&sortOrder=DESC${getSimpleEqQuery('driverId', driverId, 1)}${getSimpleEqQuery('endAt', 'null', 2)}`);
  const activeTrip = activeTrips?.results?.[0];
  const { data: activeTripData } = useGetActiveTripById(fleetId, activeTrip);
  const { data: alerts, isSuccess: alertsLoaded } = useGetAlerts(
    fleetId,
    queryFilters(
      activeFilters,
      undefined,
      getSimpleEqQuery('driverId', driverId as string, 20),
      'eventDate',
      ENTITY_DETAILS_MAP_ALERTS_LIMIT,
    ),
    !!driverId,
  );

  const handleBackToList = (): void => navigate('/drivers/');

  const handleGoBack = (): void => {
    if (listNavigation.backToList) {
      listNavigation.backToList();
      return;
    }
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      handleBackToList();
    }
  };

  const onSubmitSuccess = (): void => {
    togglePostLearnDriverOpen(true);
  };
  const onSubmitFail = (err: string): void => {
    logger.error('DriverDetails', 'On submit fail', err);
    if (err.startsWith('Wrong current driversLearnFace. Status: null')) {
      setLearnFaceInProgress(true);
    }
  };

  const { mutate: mutateLearnFace, isLoading: isLearnFaceLoading } = usePostLearnDriverFace(
    onSubmitSuccess,
    onSubmitFail,
  );

  const { mutate: mutateDelete, isLoading: isDeleteLoading } = useDeleteDriver(
    handleBackToList,
    onSubmitFail,
  );

  const { mutate: mutateFaceDataDelete, isLoading: isFaceDataDeleteLoading } = useDeleteLearnDriverFace(
    () => { },
    onSubmitFail,
  );

  const { mutate: mutateRequestAvatar } = usePostDriverAvatarRequest(
    () => {
      if (!driver?.device?.isOnline) {
        toggleDeviceOfflineSaveModal(true);
      } else {
        toggleConfirmSubmitModal(true);
      }
    },
    (err: string): void => {
      logger.error('DriverDetails', 'On submit fail', err);
      if (err === 'Wrong DriversAvatarRequest status: pending') {
        setRequestAvatarInProgress(true);
      }
    },
  );

  const requestLearnFace = (force?: boolean): void => mutateLearnFace({
    fleetId,
    driverId: driverId as string,
    deviceId: driver?.device?.deviceId as string,
    learnFaceRequestedBy: username,
    force,
  });

  const handleLearnFace = (): void => {
    if (driver?.faceData) {
      toggleConfirmLearnFaceOpen(true);
    } else {
      requestLearnFace();
    }
  };

  Object.keys(alertRisksChartConfig.cards).forEach((key): void => {
    alertRisksChartConfig.cards[key as AlertRisksKeys]!.count = risksChartData?.[key as AlertRisksKeys] || 0;
  });

  Object.keys(alertTypesChartConfig.cards).forEach((key): void => {
    alertTypesChartConfig.cards[key as AlertTypesKeys]!.count = typesChartData?.[key as AlertTypesKeys] || 0;
  });

  const periodLabel = getPeriodDisplayLabel(activeFilters);
  const scoreLabel = isSingleDayPeriod(activeFilters) ? 'Daily score' : 'Average score';
  alertRisksChartConfig.title = `Alert risks • ${periodLabel}`;
  alertTypesChartConfig.title = `Alert types • ${periodLabel}`;

  const requestDeleteFaceData = (): void => mutateFaceDataDelete({
    fleetId,
    driverId: driverId as string,
  });

  const requestDriverAvatar = (force?: boolean): void => mutateRequestAvatar({
    fleetId,
    driverId: driverId as string,
    force,
  });

  const handleDeleteFaceDataClick = (): void => {
    toggleConfirmDeleteFaceModalOpen(true);
  };

  const handleDeleteClick = (): void => {
    toggleDeleteModalOpen(true);
  };

  const handleDeleteDriver = (): void => mutateDelete({
    fleetId,
    driverId: driverId as string,
  });

  const handleEditDriver = (): void => {
    toggleEditModalOpen(true);
  };

  const handleDHTSettings = (): void => {
    toggleDHTModalOpen(true);
  };

  const handleRequestAvatar = (): void => {
    toggleRequestAvatarModalOpen(true);
  };
  const handleAddNote = (): void => {
    toggleAddNoteModalOpen(true);
  };

  if (error) {
    handleBackToList();
  }

  const reportName = `${fleetData?.name || 'fleet_na'}_drivers_${getTimeRangeText(filters || driverDetailsPageFilters)}_widgets`;

  const hiddenDriverTagItems: HiddenDetailTagItem[] = [{
    key: 'driver-details-more-tags',
    node: (
      <>
        <Tags.LabeledIcon
          flexible
          label={generateDistance(driver?.distanceInKmTotal, true)}
          icon={IconKeys.DISTANCE}
        />
        {!!tripsDuration && tripsDuration.durationInMs !== null && (
          <Tags.Label
            label={tripsDuration.durationInMs > 0 ? `Driving time: ${convertMsecToHs(tripsDuration.durationInMs)} hours in total` : 'Driving time: N/A'}
          />
        )}
        {!!averageSpeed?.averageSpeedMs && (
          <Tags.Label
            label={`Average speed: ${(averageSpeed && averageSpeed.averageSpeedMs !== null) ? convertMsToKm(averageSpeed.averageSpeedMs) : '0'} km/h in total`}
          />
        )}
        {tripsNumber?.count != null && (
          <Tags.Label
            label={`${tripsNumber.count} trips in total`}
          />
        )}
        {driverEvents?.count != null && (
          <Tags.LabeledIcon
            flexible
            label={generateEventsLabel(driverEvents.count)}
            icon={IconKeys.ALERT_CIRCLE}
          />
        )}
      </>
    ),
  }];

  const driverPhotoUrl = FeaturesManagement.productivity.thumbnails ? driver?.avatarUrl : null;

  return (
    <>
      {isLoading && (<DetailsPageSkeleton />)}

      {!!driver && (
        <S.Wrapper>
          <HeaderWrapper>
            <div className="details-row">
              <div className="overview-container">
                <div className="preview-container">
                  <Button height="44px" padding="10px 12px" onClick={handleGoBack}>
                    <Icon iconKey={IconKeys.ARROW_LEFT} />
                  </Button>
                  <Tags.ItemOverview
                    avatar={driver?.avatarUrl || driverThumbnail}
                    avatarAlt="driver"
                    onAvatarClick={driverPhotoUrl ? () => togglePhotoPreviewOpen(true) : undefined}
                    avatarActionLabel="View driver photo full size"
                    title={`${driver.firstName} ${driver.lastName}`}
                    subtitle={driver.licenseName && driver.licenseExpireAt ? `${driver.licenseName} (exp. ${driver.licenseExpireAt ? getFormattedDate(driver.licenseExpireAt) : ''})` : ''}
                  />
                  {listNavigation.controls}
                </div>
                {!!driver?.vehicle && (
                  <Tags.WithUnassign
                    link={`/vehicles/${driver.vehicle._id}`}
                    imageSrc={vehicleThumbnail}
                    title={driver.vehicle.licensePlateNumber}
                    description={`${driver.vehicle.manufacturer ? (`${driver.vehicle.manufacturer} `) : ''}${driver.vehicle.model ? (`${driver.vehicle.model} `) : ''}`}
                    apiCall={useAssignVehicleToDriver}
                    mutationConfig={{ driverId: null, fleetId, vehicleId: driver.vehicle._id }}
                  />
                )}
              </div>
              <div className="actions-wrapper">
                <div className="border-line" />
                {!isFleetViewer && (
                  <Button
                    height="44px"
                    padding="10px 12px"
                    title="Learn face"
                    disabled={!driver?.device?.isOnline || isLearnFaceLoading}
                    onClick={handleLearnFace}
                  >
                    <Icon iconKey={IconKeys.USER} />
                  </Button>
                )}
                {!isFleetViewer && driver?.faceData && (
                  <Button
                    height="44px"
                    padding="10px 12px"
                    title="Delete face data"
                    disabled={isLearnFaceLoading}
                    onClick={handleDeleteFaceDataClick}
                  >
                    <Icon iconKey={IconKeys.DELETE_USER_DATA} />
                  </Button>
                )}
                {!isLearnFaceLoading && (
                  <div className={`learn-face-status ${driver?.faceData ? 'learn-face-recognized' : 'learn-face-not-recognized'}`}>
                    <p>Face data</p>
                    {/* eslint-disable-next-line no-nested-ternary */}
                    <p>{driver?.faceData ? 'exists' : 'not exists'}</p>
                  </div>
                )}
                {isLearnFaceLoading && (
                  <div className="learn-face-status">
                    <p>Face recognition</p>
                    <p>in progress...</p>
                  </div>
                )}
                <div className="border-line" />
                {/* <Button
                  height="44px"
                  padding="10px 12px"
                  title="Add note"
                  onClick={handleAddNote}
                >
                  <Icon iconKey={IconKeys.ADD_NOTE} />
                </Button> */}
                {FeaturesManagement.features.drivingHoursTracker && (
                  <Button
                    height="44px"
                    padding="10px 12px"
                    title="DHT Settings"
                    onClick={handleDHTSettings}
                  >
                    <p>DHT</p>
                  </Button>
                )}
                {!isFleetViewer && FeaturesManagement.features.requestDriverAvatar && (
                  <Button
                    height="44px"
                    padding="10px 12px"
                    title="Request Avatar"
                    onClick={handleRequestAvatar}
                    disabled={!driver?.device}
                  >
                    <Icon iconKey={IconKeys.PORTRAIT} />
                  </Button>
                )}
                {!isFleetViewer && (
                  <Button
                    height="44px"
                    padding="10px 12px"
                    title="Edit"
                    onClick={handleEditDriver}
                  >
                    <Icon iconKey={IconKeys.EDIT} />
                  </Button>
                )}
                {!isFleetViewer && (
                  <Button
                    height="44px"
                    padding="10px 12px"
                    title="Delete driver"
                    disabled={isDeleteLoading}
                    onClick={handleDeleteClick}
                  >
                    <Icon iconKey={IconKeys.DELETE} />
                  </Button>
                )}
                <Button
                  height="44px"
                  padding="10px 12px"
                  title="Create report"
                  disabled={isDeleteLoading}
                  onClick={() => {
                    downloadFile(createCSV([{
                      ...driver as Record<string, any>,
                      ...changeObjectKeysWithPrefix({ ...risksChartData, ...typesChartData }, 'alerts_'),
                    }]), reportName, 'text/csv');
                  }}
                >
                  <Icon iconKey={IconKeys.SUMMARIZE} />
                </Button>
              </div>
              <div className="actions-container" />
            </div>
            <div className="sub-header">
              <div className="tags-row">
                {driverAllTimeAverage !== null ? (<Tags.Status label={`${Math.round(driverAllTimeAverage)} score in total`} backgroundColor={generateScoreColor(driverAllTimeAverage)} />) : (<Tags.Status label="No score" backgroundColor={theme.colors.outline} />)}
                {activeTrip && (
                  <ActiveTripTags
                    startAt={activeTrip.startAt}
                    deviceId={activeTrip.deviceId || driver?.device?.deviceId}
                    speedMs={activeTripData?.speedMs}
                    distanceInKm={activeTripData?.distanceInKm}
                    onClick={() => navigate(
                      `/trips/${(activeTrip.isMergedTrip && activeTrip.mergedTripId) ? activeTrip.mergedTripId : (activeTrip._id || '')}`,
                      { state: { isMergedTrip: !!activeTrip.isMergedTrip } },
                    )}
                  />
                )}
                <HiddenDetails items={hiddenDriverTagItems} label="More details..." />
              </div>
              <TimeSelection
                filters={filters}
                setFilters={setFilters}
                allTimeOption
              />
            </div>
          </HeaderWrapper>
          <div className="widgets-container">
            <DetailsWidget
              key={driverId}
              fleetId={fleetId}
              entity="driver"
              entityId={driverId as string}
              showTabs={{
                calendar: true,
                alert: true,
                vehicle: true,
                trip: true,
                video: true,
                note: true,
              }}
              filters={filters}
            />
            {/* <MapWidget
              fleetId={fleetId}
              getLocationsQuery={useGetDeviceLocations}
              entityId={tripData?.deviceId as string}
              entityType="driver"
              startDate={tripData?.startAt}
              endDate={tripData?.endAt || getEndOfTheDay().toISOString()}
              alerts={alerts?.results}
              alertsLoaded={alertsLoaded}
              isTrip
              hasTripVehicleMarker
            /> */}
            <MapWidget
              fleetId={fleetId}
              getLocationsQuery={useGetDriverLocations}
              entityId={driverId as string}
              entityType="driver"
              showDetailsButton={false}
              routeHistoryFilters={activeFilters}
              mapWrapperWidth={isWideMapTab ? '50%' : '403px'}
              alerts={alerts?.results}
              alertsLoaded={alertsLoaded}
            />
          </div>
          <S.Row>
            <ScoreTrendWidget
              title={`Driver score history • ${periodLabel}`}
              period={useHourlyMode ? 'daily' : (activeFilters.period?.[0] ?? 'monthly')}
              data={driverTrendData}
            />
            <div className="driver-score">
              <KnobChartWidget
                value={driverAverageScore !== null ? Math.round(driverAverageScore) : 'N/A'}
                max={100}
                subtitle={`${scoreLabel} • ${periodLabel}`}
              />
            </div>
          </S.Row>
          <S.Row>
            <S.Widget>
              <DoghnutChartWidget data={alertRisksChartConfig} />
            </S.Widget>
            <S.Widget>
              <DoghnutChartWidget data={alertTypesChartConfig} type="quad" />
            </S.Widget>
          </S.Row>
        </S.Wrapper>
      )}
      {photoPreviewOpen && !!driverPhotoUrl && (
        <ImagePreview
          src={driverPhotoUrl}
          alt={`${driver?.firstName || ''} ${driver?.lastName || ''}`.trim() || 'Driver photo'}
          onClose={() => togglePhotoPreviewOpen(false)}
        />
      )}
      {postLearnDriverOpen && (
        <Modal
          title="Info"
          state={postLearnDriverOpen}
          toggleState={togglePostLearnDriverOpen}
        >
          <LearnDriverMessage
            title="The request to learn driver face has been sent."
            onClose={() => togglePostLearnDriverOpen(false)}
          />
        </Modal>
      )}
      {editModalOpen && (
        <Modal
          title="Edit driver details"
          state={editModalOpen}
          toggleState={toggleEditModalOpen}
        >
          <EditDriver fleetId={fleetId} driver={driver} toggleModal={toggleEditModalOpen} />
        </Modal>
      )}
      {deleteModalOpen && (
        <Modal
          title="Confirm delete"
          state={deleteModalOpen}
          toggleState={toggleDeleteModalOpen}
          settings={{
            minWidth: '500px',
          }}
        >
          <DeleteModal
            title="Are you sure you want to delete this driver?"
            onClose={() => toggleDeleteModalOpen(false)}
            onDelete={handleDeleteDriver}
          />
        </Modal>
      )}
      {confirmLearnFaceModalOpen && (
        <Modal
          title="Confirm send request"
          state={confirmLearnFaceModalOpen}
          toggleState={toggleConfirmLearnFaceOpen}
          settings={{
            minWidth: '500px',
          }}
        >
          <DeleteModal
            title="Face of driver was recognized previosly. Are you sure you want to request it again?"
            onClose={() => toggleConfirmLearnFaceOpen(false)}
            onDelete={() => {
              requestLearnFace();
              toggleConfirmLearnFaceOpen(false);
            }}
            btnConfirmText="Confirm"
          />
        </Modal>
      )}
      {learnFaceInProgress && (
        <Modal
          title="Learn Face request in progress"
          state={learnFaceInProgress}
          toggleState={setLearnFaceInProgress}
          settings={{
            minWidth: '500px',
          }}
        >
          <DeleteModal
            title="Your request about learn face is in progress. Do you want to interrupt previous request and send it again?"
            onClose={() => setLearnFaceInProgress(false)}
            onDelete={() => {
              setLearnFaceInProgress(false);
              requestLearnFace(true);
            }}
            btnConfirmText="Interrupt and resend"
          />
        </Modal>
      )}
      {confirmDeleteFaceModalOpen && (
        <Modal
          title="Confirm delete request"
          state={confirmDeleteFaceModalOpen}
          toggleState={toggleConfirmDeleteFaceModalOpen}
          settings={{
            maxWidth: '500px',
          }}
        >
          <DeleteModal
            title="Removing of existing face data would require re-training in the future. Are you sure you want to delete it?"
            onClose={() => toggleConfirmDeleteFaceModalOpen(false)}
            onDelete={() => {
              requestDeleteFaceData();
              toggleConfirmDeleteFaceModalOpen(false);
            }}
            btnConfirmText="Confirm"
          />
        </Modal>
      )}
      {addNoteModalOpen && driverId && (
        <Modal
          title="Add note for driver"
          state={addNoteModalOpen}
          toggleState={toggleAddNoteModalOpen}
        >
          <CreateNote fleetId={fleetId} entityName="drivers" entityId={driverId} toggleModal={toggleAddNoteModalOpen} />
        </Modal>
      )}
      {dhtModalOpen && driverId && (
        <Modal
          title="Driving Hours Tracker - Settings"
          state={dhtModalOpen}
          settings={{ maxWidth: '500px', minWidth: '500px' }}
          toggleState={toggleDHTModalOpen}
        >
          <DrivingHoursTracker readonly={isFleetViewer} fleetId={fleetId} driverId={driverId} toggleModal={toggleDHTModalOpen} />
        </Modal>
      )}
      {requestAvatarModalOpen && driverId && (
        <Modal
          title="Confirm avatar request"
          state={requestAvatarModalOpen}
          toggleState={toggleRequestAvatarModalOpen}
          settings={{
            maxWidth: '500px',
          }}
        >
          <DeleteModal
            title={driver?.avatarUrl ? 'Requesting of new driver avatar will remove current one. Are you sure you want to do it?' : 'Do you want to request driver avatar?'}
            onClose={() => toggleRequestAvatarModalOpen(false)}
            onDelete={() => {
              requestDriverAvatar();
              toggleRequestAvatarModalOpen(false);
            }}
            btnConfirmText="Confirm"
          />
        </Modal>
      )}
      {requestAvatarInProgress && (
        <Modal
          title="Avatar request in progress"
          state={requestAvatarInProgress}
          toggleState={setRequestAvatarInProgress}
          settings={{
            minWidth: '500px',
          }}
        >
          <DeleteModal
            title="Your request about new driver avatar is in progress. Do you want to interrupt previous request and send it again?"
            onClose={() => setRequestAvatarInProgress(false)}
            onDelete={() => {
              setRequestAvatarInProgress(false);
              requestDriverAvatar(true);
            }}
            btnConfirmText="Interrupt and resend"
          />
        </Modal>
      )}
      {deviceOfflineModalOpen && (
        <Modal
          title="Device settings"
          state={deviceOfflineModalOpen}
          toggleState={toggleDeviceOfflineSaveModal}
          settings={{
            minWidth: '500px',
          }}
        >
          <InfoModal
            title="Device is offline. Changes will be applied after device become online!"
            onClose={() => toggleDeviceOfflineSaveModal(false)}
            btnConfirmText="OK"
          />
        </Modal>
      )}
      {confirmSubmitModalOpen && (
        <Modal
          title="Request"
          state={confirmSubmitModalOpen}
          toggleState={toggleConfirmSubmitModal}
          settings={{
            minWidth: '500px',
          }}
        >
          <InfoModal
            title="The request has been sent!"
            onClose={() => toggleConfirmSubmitModal(false)}
            btnConfirmText="OK"
          />
        </Modal>
      )}
    </>
  );
}

export default DriverDetailsPage;
